import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch, supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, Users, PieChart, Link as LinkIcon, MessageSquareWarning, CheckCircle2, Copy } from "lucide-react";
import { PageHeader, StatusBadge, EmptyState, formatDate } from "@/lib/ui";
import { Typeahead } from "@/components/Typeahead";

const COMM_METHODS = ["Email", "Phone", "Text", "Slack", "Microsoft Teams", "Other"];
const TOOLS = ["Google Workspace", "Google Calendar", "Microsoft 365", "Outlook", "Zoom", "Microsoft Teams", "Slack", "Canva", "QuickBooks", "CRM", "Project Management Software", "Cloud Storage", "Other"];
const STATUSES = ["not_started", "in_progress", "submitted", "under_review", "approved", "complete"];

const emptyForm = () => ({
  business_info: { name: "", website: "", industry: "", description: "", time_zone: "", business_hours: "" },
  services_priorities: { services_being_provided: "", top_priorities: "", recurring_tasks: "", current_challenges: "", short_term_goals: "", long_term_goals: "", special_instructions: "" },
  communication_prefs: { methods: [], best_days: "", best_times: "", response_method: "", meeting_required: false, meeting_frequency: "", meeting_day: "", meeting_time: "" },
  tools_platforms: { tools: [], other_tools: "", additional_instructions: "" },
  work_preferences: { prioritization: "", communication_style: "", approval_process: "", approver: "", deadlines: "", additional: "" },
  final_confirmation: { additional_info: "", confirmed: false, signature: "", signed_at: "" },
});

// Percent of onboarding fields filled across sections A-G.
// Weighted so every section contributes roughly equally to the score.
function computeCompletion(form) {
  const sections = [
    ["business_info", ["name", "website", "industry", "description", "time_zone", "business_hours"]],
    ["services_priorities", ["services_being_provided", "top_priorities", "recurring_tasks", "current_challenges", "short_term_goals", "long_term_goals", "special_instructions"]],
    ["communication_prefs", ["best_days", "best_times", "response_method"]],
    ["tools_platforms", ["other_tools", "additional_instructions"]],
    ["work_preferences", ["prioritization", "communication_style", "approval_process", "approver", "deadlines", "additional"]],
    ["final_confirmation", ["signature", "confirmed"]],
  ];
  let total = 0, filled = 0;
  for (const [sec, keys] of sections) {
    for (const k of keys) {
      total += 1;
      const v = form?.[sec]?.[k];
      if (v === true || (typeof v === "string" && v.trim().length > 0)) filled += 1;
    }
  }
  // Extra credit for at least one selection in checkbox arrays (methods, tools)
  total += 2;
  if ((form?.communication_prefs?.methods || []).length > 0) filled += 1;
  if ((form?.tools_platforms?.tools || []).length > 0) filled += 1;
  return Math.round((filled / total) * 100);
}

export default function ClientOnboarding() {
  const { currentOrgId } = useAuth();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [client, setClient] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [status, setStatus] = useState("in_progress");

  const completion = useMemo(() => computeCompletion(form), [form]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/onboarding?organization_id=${currentOrgId}`);
      setRows(r);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { if (currentOrgId) load(); /* eslint-disable-next-line */ }, [currentOrgId]);

  // Deep-link: open ?id=xxx onboarding row automatically once rows load
  useEffect(() => {
    const id = params.get("id");
    if (!id || !rows.length || dialogOpen) return;
    const row = rows.find((r) => r.id === id);
    if (row) { openExisting(row); params.delete("id"); setParams(params, { replace: true }); }
    /* eslint-disable-next-line */
  }, [rows, params.get("id")]);

  const openFor = async (clientRow) => {
    setEditing(null); setClient(clientRow); setForm(emptyForm()); setStatus("in_progress");
    try {
      const existing = await apiFetch(`/onboarding/by-client/${clientRow.id}`);
      if (existing) {
        setEditing(existing);
        setForm({
          business_info: { name: clientRow.referral_source || "", website: "", industry: "", description: "", time_zone: "", business_hours: "", ...(existing.business_info || {}) },
          services_priorities: { ...emptyForm().services_priorities, ...(existing.services_priorities || {}) },
          communication_prefs: { ...emptyForm().communication_prefs, ...(existing.communication_prefs || {}) },
          tools_platforms: { ...emptyForm().tools_platforms, ...(existing.tools_platforms || {}) },
          work_preferences: { ...emptyForm().work_preferences, ...(existing.work_preferences || {}) },
          final_confirmation: { ...emptyForm().final_confirmation, ...(existing.final_confirmation || {}) },
        });
        setStatus(existing.status);
      } else {
        setForm((f) => ({ ...f, business_info: { ...f.business_info, name: clientRow.referral_source || "" } }));
      }
    } catch (_) {}
    setDialogOpen(true);
  };

  const openExisting = (row) => {
    openFor(row.clients);
  };

  const sendLink = async () => {
    if (!client) return;
    try {
      const r = await apiFetch("/onboarding/send-link", { method: "POST", body: JSON.stringify({ organization_id: currentOrgId, client_id: client.id }) });
      const link = r.link || `${window.location.origin}/portal/onboarding/${r.token}`;
      await navigator.clipboard.writeText(link).catch(() => {});
      toast.success(r.email_sent ? "Link emailed (and copied to clipboard)" : "Link generated & copied to clipboard");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const requestChanges = async () => {
    if (!editing) return;
    const section = window.prompt("Which section needs changes? Enter one letter (A-G):", "A");
    if (!section) return;
    const upperSection = section.trim().toUpperCase();
    if (!"ABCDEFG".includes(upperSection)) return toast.error("Enter A, B, C, D, E, F, or G");
    const note = window.prompt(`What should the client update in Section ${upperSection}?`);
    if (!note?.trim()) return;
    try {
      await apiFetch(`/onboarding/${editing.id}/request-changes`, { method: "POST", body: JSON.stringify({ section: upperSection, note: note.trim() }) });
      toast.success("Change request sent");
      setDialogOpen(false); load();
    } catch (e) { toast.error(e.message); }
  };

  const approve = async () => {
    if (!editing) return;
    try {
      await apiFetch(`/onboarding/${editing.id}/approve`, { method: "POST" });
      toast.success("Onboarding approved");
      setDialogOpen(false); load();
    } catch (e) { toast.error(e.message); }
  };

  const save = async (newStatus) => {
    if (!client) return;
    if (newStatus === "submitted" && !form.final_confirmation.confirmed) return toast.error("Client confirmation is required before submitting");
    if (newStatus === "submitted" && !form.final_confirmation.signature?.trim()) return toast.error("Electronic signature is required before submitting");
    const payload = {
      organization_id: currentOrgId, client_id: client.id,
      business_info: form.business_info,
      services_priorities: form.services_priorities,
      communication_prefs: form.communication_prefs,
      tools_platforms: form.tools_platforms,
      work_preferences: form.work_preferences,
      document_ids: [], // documents already tied to client via /app/documents
      final_confirmation: { ...form.final_confirmation, signed_at: form.final_confirmation.signed_at || (newStatus === "submitted" ? new Date().toISOString() : "") },
      status: newStatus || status,
    };
    try {
      await apiFetch(`/onboarding`, { method: "POST", body: JSON.stringify(payload) });
      toast.success(newStatus === "submitted" ? "Onboarding submitted" : "Onboarding saved");
      setDialogOpen(false); load();
    } catch (e) { toast.error(e.message); }
  };

  const toggleArr = (section, key, v) => setForm((f) => {
    const cur = f[section][key] || [];
    return { ...f, [section]: { ...f[section], [key]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] } };
  });

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="onboarding-page">
      <PageHeader title="Client Onboarding" subtitle="After the client agrees to services, complete their onboarding profile."
        actions={
          <div className="w-72">
            <Typeahead value={null}
              fetchFn={async (search) => {
                let q = supabase.from("clients").select("id,first_name,last_name,email,phone,referral_source").eq("organization_id", currentOrgId).limit(8);
                if (search) q = q.or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,email.ilike.%${search}%`);
                const { data } = await q; return data || [];
              }}
              displayFn={(c) => `${c.first_name} ${c.last_name}`}
              secondaryFn={(c) => c.email || ""}
              onChange={(c) => c && openFor(c)}
              placeholder="Start onboarding for…" testid="onb-start" />
          </div>
        } />

      {loading ? (
        <div className="text-center py-16 text-[#57534E]">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={UserPlus} title="No onboarding records yet"
          description="Search for a client above to start the onboarding form. It pre-fills anything already in the client record." />
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF8F5] text-xs uppercase tracking-widest text-[#57534E]">
              <tr><th className="text-left px-4 py-3">Client</th><th className="text-left px-4 py-3">Business</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Updated</th></tr>
            </thead>
            <tbody className="divide-y divide-[#E8E2D9]">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`onb-row-${r.id}`} onClick={() => openExisting(r)} className="hover:bg-[#FAF8F5] cursor-pointer">
                  <td className="px-4 py-3 font-medium">{r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : "—"}</td>
                  <td className="px-4 py-3 text-[#57534E]">{r.business_info?.name || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge value={r.status} /></td>
                  <td className="px-4 py-3 text-[#57534E]">{formatDate(r.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{client ? `Onboarding — ${client.first_name} ${client.last_name}` : "Onboarding"}</DialogTitle>
            <DialogDescription className="text-xs text-[#57534E]">Do not ask for passwords, API keys, or security codes anywhere on this form.</DialogDescription>
          </DialogHeader>

          {/* Completion meter — updates live as staff fills sections */}
          <div className="mt-1 mb-2" data-testid="onb-progress">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="uppercase tracking-widest text-[#57534E]">Completion</span>
              <span className="font-semibold text-[#2C3A29]">{completion}%</span>
            </div>
            <div className="w-full h-2 rounded-full bg-[#F5F1EA] overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#E8712C] to-[#C55A1F] transition-all duration-500"
                style={{ width: `${completion}%` }}
              />
            </div>
          </div>

          <Section title="A. Business Information" icon={ShieldCheck}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Business/Organization name"><Input data-testid="onb-biz-name" value={form.business_info.name} onChange={(e) => setForm((f) => ({ ...f, business_info: { ...f.business_info, name: e.target.value } }))} /></Field>
              <Field label="Website"><Input data-testid="onb-biz-web" value={form.business_info.website} onChange={(e) => setForm((f) => ({ ...f, business_info: { ...f.business_info, website: e.target.value } }))} /></Field>
              <Field label="Industry"><Input value={form.business_info.industry} onChange={(e) => setForm((f) => ({ ...f, business_info: { ...f.business_info, industry: e.target.value } }))} /></Field>
              <Field label="Time zone *"><Input placeholder="e.g. America/New_York" data-testid="onb-tz" value={form.business_info.time_zone} onChange={(e) => setForm((f) => ({ ...f, business_info: { ...f.business_info, time_zone: e.target.value } }))} /></Field>
              <Field label="Business description" wide><Textarea rows={2} value={form.business_info.description} onChange={(e) => setForm((f) => ({ ...f, business_info: { ...f.business_info, description: e.target.value } }))} /></Field>
              <Field label="Business hours" wide><Input placeholder="e.g. Mon-Fri 9am-5pm" value={form.business_info.business_hours} onChange={(e) => setForm((f) => ({ ...f, business_info: { ...f.business_info, business_hours: e.target.value } }))} /></Field>
            </div>
          </Section>

          <Section title="B. Services & Priorities" icon={PieChart}>
            {[
              ["services_being_provided", "Services being provided *"],
              ["top_priorities", "Top priorities"],
              ["recurring_tasks", "Important recurring tasks"],
              ["current_challenges", "Current challenges"],
              ["short_term_goals", "Short-term goals"],
              ["long_term_goals", "Long-term goals"],
              ["special_instructions", "Special instructions"],
            ].map(([k, l]) => (
              <Field key={k} label={l}><Textarea rows={2} data-testid={`onb-sp-${k}`} value={form.services_priorities[k]} onChange={(e) => setForm((f) => ({ ...f, services_priorities: { ...f.services_priorities, [k]: e.target.value } }))} /></Field>
            ))}
          </Section>

          <Section title="C. Communication Preferences" icon={Users}>
            <Field label="Preferred communication method">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
                {COMM_METHODS.map((m) => (
                  <label key={m} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${(form.communication_prefs.methods || []).includes(m) ? "bg-[#FDE9D8] border-[#F0C69A]" : "bg-white border-[#E8E2D9]"}`}>
                    <input type="checkbox" checked={(form.communication_prefs.methods || []).includes(m)} onChange={() => toggleArr("communication_prefs", "methods", m)} className="accent-[#E8712C]" />{m}
                  </label>
                ))}
              </div>
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Best days to contact"><Input value={form.communication_prefs.best_days} onChange={(e) => setForm((f) => ({ ...f, communication_prefs: { ...f.communication_prefs, best_days: e.target.value } }))} /></Field>
              <Field label="Best times to contact"><Input value={form.communication_prefs.best_times} onChange={(e) => setForm((f) => ({ ...f, communication_prefs: { ...f.communication_prefs, best_times: e.target.value } }))} /></Field>
              <Field label="Preferred response method"><Input value={form.communication_prefs.response_method} onChange={(e) => setForm((f) => ({ ...f, communication_prefs: { ...f.communication_prefs, response_method: e.target.value } }))} /></Field>
              <Field label="Regular meeting required?">
                <Select value={form.communication_prefs.meeting_required ? "yes" : "no"} onValueChange={(v) => setForm((f) => ({ ...f, communication_prefs: { ...f.communication_prefs, meeting_required: v === "yes" } }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
            {form.communication_prefs.meeting_required && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Meeting frequency"><Input placeholder="e.g. Weekly" value={form.communication_prefs.meeting_frequency} onChange={(e) => setForm((f) => ({ ...f, communication_prefs: { ...f.communication_prefs, meeting_frequency: e.target.value } }))} /></Field>
                <Field label="Preferred meeting day"><Input value={form.communication_prefs.meeting_day} onChange={(e) => setForm((f) => ({ ...f, communication_prefs: { ...f.communication_prefs, meeting_day: e.target.value } }))} /></Field>
                <Field label="Preferred meeting time"><Input value={form.communication_prefs.meeting_time} onChange={(e) => setForm((f) => ({ ...f, communication_prefs: { ...f.communication_prefs, meeting_time: e.target.value } }))} /></Field>
              </div>
            )}
          </Section>

          <Section title="D. Tools & Platforms" icon={PieChart}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
              {TOOLS.map((t) => (
                <label key={t} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${(form.tools_platforms.tools || []).includes(t) ? "bg-[#FDE9D8] border-[#F0C69A]" : "bg-white border-[#E8E2D9]"}`}>
                  <input type="checkbox" checked={(form.tools_platforms.tools || []).includes(t)} onChange={() => toggleArr("tools_platforms", "tools", t)} className="accent-[#E8712C]" />{t}
                </label>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <Field label="Other tools"><Input value={form.tools_platforms.other_tools} onChange={(e) => setForm((f) => ({ ...f, tools_platforms: { ...f.tools_platforms, other_tools: e.target.value } }))} /></Field>
              <Field label="Additional instructions"><Input value={form.tools_platforms.additional_instructions} onChange={(e) => setForm((f) => ({ ...f, tools_platforms: { ...f.tools_platforms, additional_instructions: e.target.value } }))} /></Field>
            </div>
            <p className="text-[11px] text-[#B02222] mt-3">Never enter passwords, API keys, or security codes here.</p>
          </Section>

          <Section title="E. Work Preferences" icon={Users}>
            {[
              ["prioritization", "How should tasks be prioritized?"],
              ["communication_style", "Preferred communication style"],
              ["approval_process", "Approval process"],
              ["approver", "Who approves completed work?"],
              ["deadlines", "Important deadlines"],
              ["additional", "Additional work preferences"],
            ].map(([k, l]) => (
              <Field key={k} label={l}><Textarea rows={2} value={form.work_preferences[k]} onChange={(e) => setForm((f) => ({ ...f, work_preferences: { ...f.work_preferences, [k]: e.target.value } }))} /></Field>
            ))}
          </Section>

          <Section title="F. Documents" icon={ShieldCheck}>
            <p className="text-sm text-[#57534E]">Upload SOPs, brand guidelines and reference files from the <b>Documents</b> tab on this client's profile. All uploads are automatically linked to the client record.</p>
          </Section>

          <Section title="G. Final Confirmation" icon={ShieldCheck}>
            <Field label="Additional information"><Textarea rows={2} value={form.final_confirmation.additional_info} onChange={(e) => setForm((f) => ({ ...f, final_confirmation: { ...f.final_confirmation, additional_info: e.target.value } }))} /></Field>
            <label className="flex items-start gap-3 p-3 rounded-xl border border-[#E8E2D9] bg-white cursor-pointer">
              <input type="checkbox" checked={!!form.final_confirmation.confirmed} onChange={(e) => setForm((f) => ({ ...f, final_confirmation: { ...f.final_confirmation, confirmed: e.target.checked } }))} className="w-4 h-4 mt-0.5 accent-[#E8712C]" data-testid="onb-confirm" />
              <span className="text-sm">I confirm the information provided in this onboarding form is accurate and complete.</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Electronic signature (full name)"><Input data-testid="onb-signature" value={form.final_confirmation.signature} onChange={(e) => setForm((f) => ({ ...f, final_confirmation: { ...f.final_confirmation, signature: e.target.value } }))} /></Field>
              <Field label="Date signed"><Input readOnly value={form.final_confirmation.signed_at ? new Date(form.final_confirmation.signed_at).toLocaleDateString() : new Date().toLocaleDateString()} className="bg-[#FAF8F5]" /></Field>
            </div>
          </Section>

          <div className="flex flex-wrap justify-end gap-2 pt-4 border-t border-[#E8E2D9] mt-4">
            <Button variant="outline" className="rounded-full" onClick={() => setDialogOpen(false)}>Close</Button>
            {client && editing && (
              <>
                <Button variant="outline" className="rounded-full" onClick={sendLink} data-testid="onb-send-link">
                  <LinkIcon size={13} className="mr-1.5" /> Send / copy link
                </Button>
                <Button variant="outline" className="rounded-full text-[#C55A1F] border-[#F0C69A]" onClick={requestChanges} data-testid="onb-request-changes">
                  <MessageSquareWarning size={13} className="mr-1.5" /> Request changes
                </Button>
                {["submitted", "resubmitted", "under_review"].includes(editing.status) && (
                  <Button className="rounded-full bg-[#2C3A29] hover:bg-[#3E4C38] text-white" onClick={approve} data-testid="onb-approve">
                    <CheckCircle2 size={13} className="mr-1.5" /> Approve
                  </Button>
                )}
              </>
            )}
            <Button variant="outline" className="rounded-full" onClick={() => save("in_progress")} data-testid="onb-save-progress">Save progress</Button>
            <Button className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]" onClick={() => save("submitted")} data-testid="onb-submit">Submit onboarding</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="border border-[#E8E2D9] rounded-2xl p-4 md:p-5 space-y-3 mt-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#57534E]">
        {Icon && <Icon size={14} strokeWidth={1.75} />}{title}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, wide, children }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label className="text-sm">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
